/* Technology hub (/pages/technology) and Technology entry pages (/pages/technology/<handle>).
   Catalogue: sections/ls-tech-catalog rendered on a collection URL (cached 10 min in sessionStorage).
   Cards: sections/ls-tech-card rendered on each product URL (Minimog product card).
   Every visible string comes from section settings (data attributes / JSON config). */
(() => {
  'use strict';

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const TTL = 10 * 60 * 1000;
  const SKIP_VARIANT = /\bCIEM\b|\bonly\b/i;
  const SOUND_ORDER = ['Warm', 'Bright', 'Basshead', 'Reference', 'Balanced', 'Vocal'];
  const CONFIG_ORDER = ['Single Driver', 'Multi-driver / single technology', 'Hybrid', 'Tribrid', 'Quadbrid', 'Multi-technology (5+)'];
  const FORMATS = ['all', 'iems', 'headphones', 'other'];
  const ARROW = (window.LsIcons && window.LsIcons.svg('arrow')) || '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M5 12h14m-6-6 6 6-6 6"/></svg>';
  const CLOSE = (window.LsIcons && window.LsIcons.svg('close')) || '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6 6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
  const PRODUCT = (window.LsIcons && window.LsIcons.svg('product')) || '<svg viewBox="0 0 40 40" aria-hidden="true" focusable="false"><path d="M10 22c-5-8-1-15 6-16 9-1 15 7 11 15l-4 8-8 2-5-9Z M13 23l-6 4 4 7 7-4 M21 6c3-4 11-2 11 5"/></svg>';

  const esc = (x) => String(x ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const fill = (tpl, map) => String(tpl || '').replace(/\{(\w+)\}/g, (m, k) => (k in map ? map[k] : m));
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const json = (el) => { try { return JSON.parse(el.textContent); } catch (e) { return null; } };

  /* ---------- Catalogue ---------- */
  const feeds = {};
  async function fetchPage(path, page) {
    const res = await fetch(path + (path.includes('?') ? '&' : '?') + 'section_id=ls-tech-catalog&page=' + page, { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Catalogue ' + res.status);
    const m = (await res.text()).match(/<script[^>]*data-ls-tech-feed[^>]*>([\s\S]*?)<\/script>/);
    if (!m) throw new Error('Catalogue feed missing');
    return JSON.parse(m[1]);
  }
  function normalize(x) {
    const format = x.f === 'Wired IEM' ? 'iems' : x.f === 'Headphones' ? 'headphones' : x.f ? 'other'
      : /headphone/i.test(x.pt || '') ? 'headphones' : 'iems';
    return {
      handle: x.h, name: x.n, brand: x.b || '', url: x.u, image: x.i || '', format, order: x.o,
      tech: x.t || [], config: x.c || '', signature: x.s || '', secondary: x.s2 || [], genres: x.g || [], uses: x.us || [],
      priority: Number.isInteger(x.fp) ? x.fp : 3,
      variants: (x.v || []).filter((v) => v[1] > 0 && !SKIP_VARIANT.test(v[0])).map((v) => ({ title: v[0], price: v[1], available: !!v[2] })),
    };
  }
  function loadCatalog(path) {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';
    const key = 'ls-tech-catalog:' + path + ':' + currency;
    if (feeds[key]) return feeds[key];
    try {
      const cached = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (cached && Date.now() - cached.at < TTL) return (feeds[key] = Promise.resolve(cached.items));
    } catch (e) { /* storage unavailable */ }
    feeds[key] = (async () => {
      const first = await fetchPage(path, 1);
      let items = first.items;
      if (first.pages > 1) {
        const rest = await Promise.all(Array.from({ length: first.pages - 1 }, (_, i) => fetchPage(path, i + 2)));
        rest.forEach((p) => { items = items.concat(p.items); });
      }
      items = items.map(normalize).filter((p) => p.variants.length);
      try { sessionStorage.setItem(key, JSON.stringify({ at: Date.now(), items })); } catch (e) { /* quota */ }
      return items;
    })();
    feeds[key].catch(() => { delete feeds[key]; });
    return feeds[key];
  }

  function moneyFormatter(cfg) {
    const currency = (cfg && cfg.currency) || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    const locale = (cfg && cfg.locale) || document.documentElement.lang || 'en';
    const make = (digits) => { try { return new Intl.NumberFormat(locale, { style: 'currency', currency, minimumFractionDigits: digits, maximumFractionDigits: 2 }); } catch (e) { return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: digits, maximumFractionDigits: 2 }); } };
    const whole = make(0), cents = make(2);
    return (n) => (n % 100 ? cents : whole).format(n / 100);
  }

  /* ---------- Schematic components (hub + entry pages) ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.lt-diagram [data-part-index]');
    if (!btn) return;
    const fig = btn.closest('.lt-diagram');
    const index = Number(btn.dataset.partIndex);
    const active = fig.dataset.focus === String(index) ? -1 : index;
    fig.dataset.focus = String(active);
    fig.querySelectorAll('[data-part-index]').forEach((b) => b.setAttribute('aria-pressed', String(Number(b.dataset.partIndex) === active)));
    const note = fig.querySelector('.lt-part-note');
    if (note) note.innerHTML = active < 0 ? esc(note.dataset.hint) : '<strong>' + esc(btn.textContent) + '.</strong> ' + esc(btn.dataset.partNote);
  });

  /* ---------- Hub ---------- */
  function cheapestAvailable(p) {
    return p.variants.filter((v) => v.available).sort((a, b) => a.price - b.price)[0] || null;
  }
  function inFormat(p, fmt) { return fmt === 'all' || p.format === fmt; }

  function initHub() {
    const explorer = document.querySelector('[data-lt-explorer]');
    const compare = document.querySelector('[data-lt-compare]');
    if (!explorer && !compare) return;
    let format = new URLSearchParams(location.search).get('type');
    if (!FORMATS.includes(format)) format = 'all';
    let items = null, failed = false;
    const views = [explorer && hubExplorer(explorer), compare && hubCompare(compare, (f) => { format = f; writeType(); update(); })].filter(Boolean);
    function writeType() {
      if (document.querySelector('[data-lt-products]')) return;
      const q = new URLSearchParams(location.search);
      if (format === 'all') q.delete('type'); else q.set('type', format);
      try { history.replaceState(null, '', location.pathname + (q.toString() ? '?' + q : '') + location.hash); } catch (e) { /* ignore */ }
    }
    function update() { views.forEach((v) => v.update(format, items, failed)); }
    update();
    const path = (compare && compare.dataset.feed) || (explorer && explorer.dataset.feed) || '/collections/all';
    loadCatalog(path).then((list) => { items = list; update(); }).catch(() => { failed = true; update(); });
  }

  function hubExplorer(el) {
    el.classList.add('lt-enhanced');
    const tabs = [...el.querySelectorAll('[data-tech-select]')];
    const panels = [...el.querySelectorAll('[data-tech-panel]')];
    const d = el.dataset;
    let current = tabs[0] && tabs[0].dataset.techSelect;
    el.querySelectorAll('[data-type-link]').forEach((a) => { a.dataset.base = a.getAttribute('href'); });
    function select(tag, focus) {
      current = tag;
      tabs.forEach((b) => {
        const on = b.dataset.techSelect === tag;
        b.setAttribute('aria-selected', String(on));
        b.tabIndex = on ? 0 : -1;
        if (on && focus) b.focus({ preventScroll: true });
      });
      panels.forEach((p) => { p.hidden = p.dataset.techPanel !== tag; });
    }
    el.addEventListener('click', (e) => { const b = e.target.closest('[data-tech-select]'); if (b) select(b.dataset.techSelect); });
    el.addEventListener('keydown', (e) => {
      const b = e.target.closest('[data-tech-select]');
      if (!b || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
      e.preventDefault();
      const shown = tabs.filter((t) => !t.hidden);
      const i = shown.indexOf(b);
      const next = e.key === 'Home' ? 0 : e.key === 'End' ? shown.length - 1 : (i + (e.key === 'ArrowRight' ? 1 : -1) + shown.length) % shown.length;
      select(shown[next].dataset.techSelect, true);
    });
    return {
      update(format, items) {
        el.querySelectorAll('[data-type-link]').forEach((a) => {
          const [base, hash] = a.dataset.base.split('#');
          a.href = base + (format === 'all' ? '' : '?type=' + format) + (hash ? '#' + hash : '');
        });
        if (!items) return;
        tabs.forEach((b) => {
          const tag = b.dataset.techSelect;
          const n = items.filter((p) => p.tech.includes(tag) && inFormat(p, format) && cheapestAvailable(p)).length;
          b.hidden = n === 0;
          const panel = panels.find((p) => p.dataset.techPanel === tag);
          const out = panel && panel.querySelector('[data-tech-count]');
          if (out) {
            out.textContent = fill(d['count' + format.charAt(0).toUpperCase() + format.slice(1)], { count: n, word: n === 1 ? d.wordOne : d.wordMany });
            out.hidden = false;
          }
        });
        const visible = tabs.filter((t) => !t.hidden);
        if (visible.length && !visible.some((t) => t.dataset.techSelect === current)) select(visible[0].dataset.techSelect);
      },
    };
  }

  function hubCompare(el, onFormat) {
    const cfg = json(el.querySelector('[data-lt-compare-config]')) || {};
    const money = moneyFormatter(cfg.money);
    const rows = [...el.querySelectorAll('[data-matrix-tech]')];
    const codes = Object.fromEntries(rows.map((r) => [r.dataset.matrixTech, r.dataset.code]));
    const scroller = el.querySelector('.tm-table-scroll');
    const box = el.querySelector('.tm-table-box');
    const empty = el.querySelector('[data-matrix-empty]');
    const status = el.querySelector('[data-matrix-status]');
    const live = el.querySelector('[data-matrix-live]');
    const caption = el.querySelector('.tm-table caption');
    el.classList.add('tm-ready');
    el.closest('.lt-page')?.classList.add('lt-enhanced');

    function edges() {
      const left = el.querySelector('[data-matrix-scroll=left]'), right = el.querySelector('[data-matrix-scroll=right]');
      el.classList.toggle('tm-overflow', scroller.scrollWidth > scroller.clientWidth + 2);
      if (left) left.disabled = scroller.scrollLeft < 2;
      if (right) right.disabled = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 2;
    }
    el.addEventListener('click', (e) => {
      const b = e.target.closest('button');
      if (!b) return;
      if (b.hasAttribute('data-matrix-format')) onFormat(b.dataset.matrixFormat);
      if (b.hasAttribute('data-matrix-scroll')) {
        const sign = b.dataset.matrixScroll === 'left' ? -1 : 1;
        scroller.scrollBy({ left: sign * Math.max(220, scroller.clientWidth - 142), behavior: reduced() ? 'auto' : 'smooth' });
      }
    });
    scroller.addEventListener('scroll', edges, { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(edges).observe(scroller);
    techDrawer(el, rows);

    function guide(row, format) { return row.dataset.url + (format === 'all' ? '' : '?type=' + format) + '#products'; }
    function cell(row, format, items, failed) {
      const tag = row.dataset.matrixTech;
      const guideLink = '<a class="tm-guide" href="' + esc(row.dataset.url) + '">' + esc(cfg.guide) + '</a>';
      if (failed) return '<div class="tm-unavailable">' + esc(cfg.error) + '</div>' + guideLink;
      if (!items) return '<div class="tm-unavailable">' + esc(cfg.loading) + '</div>' + guideLink;
      const pool = items.filter((p) => p.tech.includes(tag) && inFormat(p, format)).map((p) => ({ p, v: cheapestAvailable(p) })).filter((x) => x.v);
      if (!pool.length) return '<div class="tm-unavailable">' + esc(cfg.no_products) + '</div>' + guideLink;
      pool.sort((a, b) => a.v.price - b.v.price || a.p.name.localeCompare(b.p.name));
      const { p, v } = pool[0];
      const arch = p.tech.map((t) => codes[t] || t).join(' + ') + (p.tech.length > 1 && cfg.hybrid ? ' ' + cfg.hybrid : '');
      const type = (cfg.types || {})[p.format] || '';
      const count = pool.length === 1 ? cfg.count_one : fill(cfg.count, { count: pool.length });
      return '<div class="tm-price-top"><span class="tm-price"><small>' + esc(cfg.price_label) + '</small>' + esc(money(v.price)) + '</span>'
        + '<span class="tm-price-visual">' + PRODUCT + (p.image ? '<img alt="" loading="lazy" src="' + esc(p.image) + '">' : '') + '</span></div>'
        + '<a class="tm-product" href="' + esc(p.url) + '">' + esc(p.name) + '</a>'
        + '<p class="tm-product-meta">' + esc([type, arch].filter(Boolean).join(' · ')) + '</p>'
        + (row.dataset.shop ? '<a class="tm-shop" href="' + esc(guide(row, format)) + '">' + esc(row.dataset.shop) + ARROW + '</a>' : '')
        + '<span class="tm-count">' + esc(count) + '</span>';
    }
    return {
      update(format, items, failed) {
        el.querySelectorAll('[data-matrix-format]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.matrixFormat === format)));
        const visible = [];
        rows.forEach((row) => {
          const tag = row.dataset.matrixTech;
          const has = !items || items.some((p) => p.tech.includes(tag) && inFormat(p, format) && cheapestAvailable(p));
          const show = format !== 'headphones' || has;
          if (!show && row.contains(document.activeElement)) el.querySelector('[data-matrix-format="' + format + '"]')?.focus({ preventScroll: true });
          row.hidden = !show;
          row.classList.remove('tm-row-even', 'tm-row-last');
          if (show) { visible.push(row); row.classList.toggle('tm-row-even', visible.length % 2 === 0); }
          row.querySelector('[data-matrix-price]').innerHTML = cell(row, format, items, failed);
          const guideLink = row.querySelector('[data-matrix-guide]');
          if (guideLink) guideLink.href = row.dataset.url + (format === 'all' ? '' : '?type=' + format);
          row.querySelectorAll('.tm-price-visual img').forEach((i) => i.addEventListener('error', () => i.remove(), { once: true }));
        });
        visible.at(-1)?.classList.add('tm-row-last');
        const word = visible.length === 1 ? cfg.word_one : cfg.word_many;
        const fname = (cfg.formats || {})[format] || '';
        const text = format === 'headphones' ? fill(cfg.status_headphones, { count: visible.length, word }) : fill(cfg.status_all, { count: visible.length, word, format: fname });
        if (status) status.textContent = text;
        if (caption) caption.textContent = fill(cfg.caption, { count: visible.length, format: fname });
        if (live && live.textContent !== text) live.textContent = text;
        if (empty) empty.hidden = visible.length > 0;
        if (box) box.hidden = visible.length === 0;
        edges();
      },
    };
  }

  /* ---------- How-it-works side panel (comparison table) ---------- */
  function techDrawer(el, rows) {
    const dlg = el.querySelector('[data-lt-drawer]');
    if (!dlg || typeof dlg.showModal !== 'function') return;
    const items = [...dlg.querySelectorAll('[data-drawer-tech]')];
    const body = dlg.querySelector('[data-drawer-body]');
    const steps = [...dlg.querySelectorAll('[data-drawer-step]')];
    let current = null, opener = null, closing = 0;

    function order() { return rows.filter((r) => !r.hidden).map((r) => r.dataset.matrixTech); }
    function itemFor(tag) { return items.find((i) => i.dataset.drawerTech === tag); }
    function show(tag) {
      current = tag;
      items.forEach((i) => { i.hidden = i.dataset.drawerTech !== tag; });
      body.scrollTop = 0;
      const row = rows.find((r) => r.dataset.matrixTech === tag);
      const item = itemFor(tag);
      const guide = row && row.querySelector('[data-matrix-guide]');
      const type = guide ? new URL(guide.href, location.href).search : '';
      item.querySelectorAll('[data-drawer-guide]').forEach((a) => { a.href = row.dataset.url + type; });
      item.querySelectorAll('[data-drawer-shop]').forEach((a) => {
        if (a.getAttribute('href').charAt(0) !== '#') a.href = row.dataset.url + type + '#products';
      });
      const list = order(), i = list.indexOf(tag);
      steps.forEach((b) => {
        const next = list[i + Number(b.dataset.drawerStep)];
        b.hidden = !next;
        b.dataset.target = next || '';
        const out = b.querySelector('[data-drawer-step-name]');
        if (out && next) out.textContent = itemFor(next).dataset.name;
      });
    }
    function open(tag, from) {
      opener = from || null;
      clearTimeout(closing);
      show(tag);
      if (!dlg.open) {
        dlg.showModal();
        document.documentElement.classList.add('lt-drawer-lock');
        dlg.classList.remove('is-open');
        void dlg.offsetWidth;
      }
      dlg.classList.add('is-open');
      dlg.querySelector('[data-drawer-close]').focus({ preventScroll: true });
    }
    function close(after) {
      if (!dlg.open) return;
      dlg.classList.remove('is-open');
      const done = () => { if (dlg.open) dlg.close(); if (after) after(); };
      if (reduced() || after) done(); else closing = setTimeout(done, 280);
    }
    dlg.addEventListener('close', () => {
      document.documentElement.classList.remove('lt-drawer-lock');
      if (opener && document.contains(opener)) opener.focus({ preventScroll: true });
    });
    dlg.addEventListener('cancel', (e) => { e.preventDefault(); close(); });
    dlg.addEventListener('click', (e) => {
      if (e.target === dlg) return close();
      if (e.target.closest('[data-drawer-close]')) return close();
      const step = e.target.closest('[data-drawer-step]');
      if (step && step.dataset.target) return show(step.dataset.target);
      const a = e.target.closest('a[href^="#"]');
      if (a) {
        e.preventDefault();
        const target = document.querySelector(a.getAttribute('href'));
        close(() => { if (target) target.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'start' }); });
      }
    });
    el.addEventListener('click', (e) => {
      const b = e.target.closest('[data-drawer-open]');
      if (b) open(b.dataset.drawerOpen, b);
    });
  }

  /* ---------- Technology entry page ---------- */
  const cards = new Map();
  function card(handle) {
    if (!cards.has(handle)) {
      const p = fetch(ROOT + 'products/' + encodeURIComponent(handle) + '?section_id=ls-tech-card', { credentials: 'same-origin' })
        .then((r) => (r.ok ? r.text() : Promise.reject(new Error('Card ' + r.status))))
        .then((html) => {
          const t = document.createElement('template');
          t.innerHTML = html;
          const item = t.content.querySelector('.ls-tech-item');
          if (!item) throw new Error('Card missing');
          return item.outerHTML;
        });
      p.catch(() => cards.delete(handle));
      cards.set(handle, p);
    }
    return cards.get(handle);
  }

  function initGuide(el) {
    const cfg = json(el.querySelector('[data-lt-products-config]'));
    if (!cfg) return;
    const t = cfg.t || {};
    const money = moneyFormatter(cfg.money);
    const rate = Number(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;
    const prefs = document.querySelector('[data-lt-preferences]');
    const configLinks = document.querySelector('[data-lt-config-links]');
    const grid = el.querySelector('[data-role=products]');
    const heading = el.querySelector('[data-role=catalog-heading]');
    const select = (r) => el.querySelector('[data-role="' + r + '"]');
    const controls = Object.fromEntries([...el.querySelectorAll('[data-filter]')].map((c) => [c.dataset.filter, c]));
    const labels = Object.fromEntries(String(cfg.labels || '').split('\n').map((l) => l.split('=').map((s) => s.trim())).filter((x) => x.length === 2 && x[0]));
    const label = (v) => labels[v] || v;
    const fixed = cfg.fixed && cfg.fixed.value ? cfg.fixed : { key: 'technology', value: cfg.tag };
    const inFixed = (p) => (fixed.key === 'signature' ? p.signature === fixed.value || p.secondary.includes(fixed.value)
      : fixed.key === 'genre' ? p.genres.includes(fixed.value) : p.tech.includes(fixed.value));
    document.querySelectorAll('.ls-tech').forEach((n) => n.classList.add('lt-enhanced'));

    const nice = (n) => { if (n < 10) return Math.round(n); const step = 10 ** (Math.floor(Math.log10(n)) - 1); return Math.round(n / step) * step; };
    const bands = (cfg.bands || []).map((b) => {
      const min = rate === 1 ? b.min : nice(b.min * rate);
      const max = b.max === null ? null : rate === 1 ? b.max : nice(b.max * rate);
      const key = b.min + '-' + (b.max === null ? 'up' : b.max);
      const text = max === null ? fill(t.band_over, { min: money(min * 100) }) : fill(t.band_range, { min: money(min * 100), max: money(max * 100) });
      return { key, min: min * 100, max: max === null ? null : max * 100, text };
    });
    if (controls.band) bands.forEach((b) => controls.band.insertAdjacentHTML('beforeend', '<option value="' + esc(b.key) + '">' + esc(b.text) + '</option>'));

    const BASE = { type: 'all', band: '', signature: '', genre: '', use: '', configuration: '', q: '', sort: 'recommended', stock: '' };
    const norm = (x) => ({
      type: FORMATS.includes(x.type) ? x.type : 'all',
      band: bands.some((b) => b.key === x.band) ? x.band : '',
      signature: String(x.signature || ''), genre: String(x.genre || ''), use: String(x.use || ''), configuration: String(x.configuration || ''),
      q: String(x.q || '').slice(0, 150),
      sort: ['price-asc', 'price-desc', 'name'].includes(x.sort) ? x.sort : 'recommended',
      stock: x.stock === 'all' ? 'all' : '',
    });
    const readURL = () => norm(Object.fromEntries(new URLSearchParams(location.search)));
    let state = readURL(), limit = cfg.pageSize || 8, pool = null, rows = [], token = 0, searchTimer;

    function offers(p, c) {
      const band = bands.find((b) => b.key === c.band);
      return p.variants.filter((v) => (c.stock === 'all' || v.available) && (!band || (v.price > band.min && (band.max === null || v.price <= band.max))))
        .sort((a, b) => a.price - b.price);
    }
    function matches(p, c) {
      if (c.type !== 'all' && p.format !== c.type) return false;
      if (c.signature && p.signature !== c.signature && !p.secondary.includes(c.signature)) return false;
      if (c.genre && !p.genres.includes(c.genre)) return false;
      if (c.use && !p.uses.includes(c.use)) return false;
      if (c.configuration && p.config !== c.configuration) return false;
      if (c.q && !(p.name + ' ' + p.brand).toLowerCase().includes(c.q.toLowerCase())) return false;
      return offers(p, c).length > 0;
    }
    function results(c) {
      const out = pool.filter((p) => matches(p, c)).map((p) => ({ p, v: offers(p, c) }));
      out.sort((a, b) => {
        if (c.sort === 'price-asc') return a.v[0].price - b.v[0].price || a.p.name.localeCompare(b.p.name);
        if (c.sort === 'price-desc') return b.v[0].price - a.v[0].price || a.p.name.localeCompare(b.p.name);
        if (c.sort === 'name') return a.p.name.localeCompare(b.p.name);
        const avail = (x) => (x.v.some((v) => v.available) ? 1 : 0);
        const lead = c.signature || (fixed.key === 'signature' ? fixed.value : '');
        const primary = (x) => (lead && x.p.signature === lead ? 1 : 0);
        return avail(b) - avail(a) || primary(b) - primary(a) || b.p.priority - a.p.priority || a.p.order - b.p.order;
      });
      return out;
    }
    const count = (c) => pool.filter((p) => matches(p, c)).length;
    const facet = (key, value) => norm({ type: state.type, band: state.band, stock: state.stock, [key]: value });
    const href = (c) => {
      const q = new URLSearchParams();
      Object.entries(norm(c)).forEach(([k, v]) => { if (v && v !== BASE[k]) q.set(k, v); });
      return location.pathname + (q.toString() ? '?' + q : '') + '#' + (el.querySelector('.lt-product-section')?.id || 'products');
    };
    function values(key) {
      const seen = new Map();
      pool.forEach((p) => {
        const list = key === 'signature' ? [p.signature, ...p.secondary] : key === 'genre' ? p.genres : key === 'use' ? p.uses : [p.config];
        list.filter(Boolean).forEach((v) => seen.set(v, true));
      });
      const order = key === 'signature' ? SOUND_ORDER : key === 'configuration' ? CONFIG_ORDER : [];
      return [...seen.keys()].sort((a, b) => {
        const ia = order.indexOf(a), ib = order.indexOf(b);
        return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.localeCompare(b);
      });
    }

    function writeURL() {
      const q = new URLSearchParams();
      Object.entries(state).forEach(([k, v]) => { if (v && v !== BASE[k]) q.set(k, v); });
      try { history.replaceState(null, '', location.pathname + (q.toString() ? '?' + q : '') + location.hash); } catch (e) { /* ignore */ }
    }
    function updateOptions() {
      ['signature', 'genre', 'use', 'configuration'].forEach((key) => {
        const sel = controls[key];
        if (!sel) return;
        const first = sel.options[0].outerHTML;
        const vals = values(key);
        if (state[key] && !vals.includes(state[key])) vals.push(state[key]);
        sel.innerHTML = first + vals.map((v) => {
          const n = count({ ...state, [key]: v });
          return '<option value="' + esc(v) + '"' + (n === 0 && state[key] !== v ? ' disabled' : '') + '>' + esc(fill(t.any_count, { label: label(v), count: n })) + '</option>';
        }).join('');
        sel.closest('label').hidden = vals.length === 0;
      });
      [['type', FORMATS.slice(1)], ['band', bands.map((b) => b.key)]].forEach(([key, list]) => {
        const sel = controls[key];
        if (!sel) return;
        [...sel.options].forEach((o) => {
          if (!list.includes(o.value)) return;
          const n = count({ ...state, [key]: o.value });
          o.disabled = n === 0 && state[key] !== o.value;
          if (!o.dataset.text) o.dataset.text = o.textContent;
          o.textContent = fill(t.any_count, { label: o.dataset.text, count: n });
        });
      });
      Object.entries(controls).forEach(([k, c]) => { c.value = state[k] || (k === 'type' ? 'all' : k === 'sort' ? 'recommended' : ''); });
    }
    function chips() {
      const box = select('selection');
      if (!box) return;
      const band = bands.find((b) => b.key === state.band);
      const typeText = state.type !== 'all' && controls.type ? controls.type.querySelector('option[value="' + state.type + '"]')?.dataset.text : '';
      const list = [['signature', state.signature && label(state.signature)], ['genre', state.genre && label(state.genre)], ['use', state.use && label(state.use)],
        ['configuration', state.configuration && label(state.configuration)], ['type', typeText], ['band', band && band.text], ['stock', state.stock && controls.stock ? controls.stock.selectedOptions[0]?.textContent : ''],
        ['q', state.q && fill(t.search_chip, { q: state.q })]].filter((x) => x[1]);
      const rm = box.dataset.remove || '';
      box.innerHTML = '<strong>' + esc(box.dataset.label) + '</strong>' + (box.dataset.fixed ? '<span class="lt-chip lt-chip--fixed">' + esc(box.dataset.fixed) + '</span>' : '')
        + list.map(([k, l]) => '<button type="button" class="lt-chip" data-chip-clear="' + k + '" aria-label="' + esc(fill(rm, { label: l }) || l) + '"><span>' + esc(l) + '</span>' + CLOSE + '</button>').join('');
      const adv = select('advanced-count');
      if (adv) {
        const n = ['signature', 'genre', 'use', 'configuration'].filter((k) => state[k] && controls[k]).length;
        adv.textContent = n;
        adv.hidden = !n;
      }
      Object.entries(controls).forEach(([k, c]) => {
        const v = state[k] || '';
        c.closest('.lt-field')?.classList.toggle('is-set', !!v && v !== 'all' && v !== 'recommended');
      });
      const clear = select('clear');
      if (clear) clear.hidden = !list.length && (state.sort || 'recommended') === 'recommended';
    }
    function finder() {
      const a = select('finder-context');
      if (!a || !cfg.finder) return;
      const other = state.type === 'other';
      const q = new URLSearchParams({ [fixed.key]: fixed.value, from: fixed.from || 'technology' });
      if (state.signature) q.set(fixed.key === 'signature' ? 'also' : 'signature', state.signature);
      if (state.genre) q.append('genre', state.genre);
      if (state.use) q.set('use', state.use);
      if (state.type === 'iems' || state.type === 'headphones') q.set('scope', state.type);
      a.href = other ? cfg.finder : cfg.finder + '#' + q.toString();
      const span = a.querySelector('span');
      if (span) span.textContent = other ? t.finder_label_other : t.finder_label;
      const note = select('finder-note');
      if (note) note.textContent = other ? t.finder_note_other : t.finder_note;
    }
    function renderPrefs() {
      if (configLinks) {
        const list = values('configuration').map((v) => [v, count(facet('configuration', v))]).filter((x) => x[1] > 0);
        configLinks.innerHTML = list.length ? '<span>' + esc(configLinks.dataset.label) + '</span> ' + list.map(([v, n]) =>
          '<a href="' + esc(href(facet('configuration', v))) + '" data-catalog-facet="configuration" data-value="' + esc(v) + '">' + esc(label(v)) + ' (' + n + ')</a>').join('') : '';
        configLinks.hidden = !list.length;
      }
      if (!prefs) return;
      const pd = prefs.dataset;
      let shown = 0;
      prefs.querySelectorAll('[data-sound]').forEach((card) => {
        const v = card.dataset.sound;
        const n = count(facet('signature', v));
        card.hidden = n === 0;
        if (n) shown += 1;
        const out = card.querySelector('[data-sound-count]');
        if (out) out.textContent = fill(pd.count, { count: n });
        const link = card.querySelector('[data-catalog-facet]');
        if (link) link.href = href(facet('signature', v));
      });
      const missing = prefs.querySelector('[data-sound-missing]');
      if (missing) missing.hidden = shown > 0;
      ['genre', 'use'].forEach((key) => {
        const box = prefs.querySelector('[data-links="' + key + '"]');
        if (!box) return;
        const list = values(key).map((v) => [v, count(facet(key, v))]).filter((x) => x[1] > 0);
        box.innerHTML = list.length ? list.map(([v, n]) => '<a href="' + esc(href(facet(key, v))) + '" data-catalog-facet="' + key + '" data-value="' + esc(v) + '">'
          + fill(pd.link, { label: esc(label(v)), count: n }) + '</a>').join('') : '<p class="lt-note">' + esc(key === 'genre' ? pd.noneGenre : pd.noneUse) + '</p>';
      });
    }
    function fallbackCard(p) {
      return '<div class="m-product-item ls-tech-item ls-tech-fallback" data-product="' + esc(p.handle) + '"><a href="' + esc(p.url) + '">'
        + (p.image ? '<img src="' + esc(p.image) + '" alt="" loading="lazy">' : '') + '<span>' + esc(p.name) + '</span><span>' + esc(t.card_error) + '</span></a></div>';
    }
    function renderGrid() {
      const mine = ++token;
      const visible = rows.slice(0, limit);
      const keep = new Map([...grid.children].map((n) => [n.dataset.product, n]));
      const frag = document.createDocumentFragment();
      const pending = [];
      visible.forEach(({ p }) => {
        let node = keep.get(p.handle);
        if (!node) {
          node = document.createElement('div');
          node.className = 'm-product-item ls-tech-item ls-tech-skeleton';
          node.dataset.product = p.handle;
          node.setAttribute('aria-hidden', 'true');
          node.innerHTML = '<div class="ls-tech-skeleton__media ls-skel"></div><div class="ls-tech-skeleton__line ls-skel"></div><div class="ls-tech-skeleton__line ls-tech-skeleton__line--sub ls-skel"></div><div class="ls-tech-skeleton__line ls-tech-skeleton__line--short ls-skel"></div>';
          pending.push([p, node]);
        }
        frag.appendChild(node);
      });
      grid.replaceChildren(frag);
      pending.forEach(([p, node]) => {
        card(p.handle).then((html) => html, () => fallbackCard(p)).then((html) => {
          if (mine !== token || !node.isConnected) return;
          const tpl = document.createElement('template');
          tpl.innerHTML = html;
          const fresh = tpl.content.firstElementChild;
          if (fresh) node.replaceWith(fresh);
        });
      });
    }
    function render() {
      if (!pool) return;
      rows = results(state);
      const n = rows.length;
      select('result-count').textContent = n === 1 ? t.results_one : fill(t.results, { count: n });
      updateOptions();
      chips();
      renderGrid();
      select('empty').hidden = n > 0;
      const more = select('more');
      if (more) more.hidden = limit >= n;
      const shown = select('shown');
      if (shown) shown.textContent = n ? fill(t.shown, { shown: Math.min(limit, n), count: n }) : '';
      finder();
      renderPrefs();
    }
    function setState(next, { scroll = false } = {}) {
      state = norm(next);
      limit = cfg.pageSize || 8;
      render();
      writeURL();
      if (scroll && heading) {
        heading.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'start' });
        heading.focus({ preventScroll: true });
      }
    }

    el.addEventListener('change', (e) => {
      const c = e.target.closest('[data-filter]');
      if (c && c.dataset.filter !== 'q') setState({ ...state, [c.dataset.filter]: c.value });
    });
    el.addEventListener('input', (e) => {
      const c = e.target.closest('[data-filter=q]');
      if (!c) return;
      clearTimeout(searchTimer);
      searchTimer = setTimeout(() => setState({ ...state, q: c.value }), 160);
    });
    el.addEventListener('click', (e) => {
      const chip = e.target.closest('[data-chip-clear]');
      if (chip) {
        const k = chip.dataset.chipClear;
        const next = [...chip.parentNode.querySelectorAll('[data-chip-clear]')].indexOf(chip);
        setState({ ...state, [k]: k === 'type' ? 'all' : '' });
        const left = select('selection')?.querySelectorAll('[data-chip-clear]');
        (left && left.length ? left[Math.min(left.length - 1, next)] : controls[k])?.focus({ preventScroll: true });
        return;
      }
      if (e.target.closest('[data-action=clear]')) {
        setState({});
        const live = select('result-count');
        if (live && t.cleared) live.textContent = fill(t.cleared, { name: cfg.name }) + ' ' + live.textContent;
        controls.type?.focus({ preventScroll: true });
      }
      if (e.target.closest('[data-role=more]')) {
        const before = limit;
        limit += cfg.pageSize || 8;
        render();
        grid.children[before]?.querySelector('a[href]')?.focus({ preventScroll: true });
      }
    });
    document.addEventListener('click', (e) => {
      const a = e.target.closest('[data-catalog-facet], [data-all-products]');
      if (!a || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      e.preventDefault();
      if (!pool) return;
      if (a.hasAttribute('data-all-products')) setState({ type: state.type, band: state.band, stock: state.stock }, { scroll: true });
      else setState(facet(a.dataset.catalogFacet, a.dataset.value), { scroll: true });
    });
    window.addEventListener('popstate', () => { state = readURL(); limit = cfg.pageSize || 8; render(); });

    const loaded = () => select('result-count')?.classList.remove('ls-loading');
    loadCatalog(cfg.feed).then((items) => {
      pool = items.filter(inFixed);
      loaded();
      render();
    }).catch(() => {
      loaded();
      grid.querySelectorAll('[data-ls-initial]').forEach((n) => n.remove());
      select('result-count').textContent = t.error;
    });
  }

  /* ---------- Sound banner ---------- */
  function initBridge(el) {
    const chips = [...el.querySelectorAll('[data-bridge-sound]')];
    if (!chips.length) return;
    const d = el.dataset;
    const curve = el.querySelector('[data-bridge-curve]');
    const area = el.querySelector('[data-bridge-area]');
    const line = el.querySelector('[data-bridge-line]');
    const cta = el.querySelector('[data-bridge-cta]');
    const ctaLabel = el.querySelector('[data-bridge-cta-label]');
    const countOut = el.querySelector('[data-bridge-count]');
    const W = 600, MID = 110, DB = 9;
    const gains = (chip) => {
      const g = String(chip.dataset.curve || '').split(',').map(Number).filter((n) => !Number.isNaN(n));
      return g.length >= 2 ? g : [0, 0, 0, 0, 0, 0, 0];
    };
    const resample = (g, n) => Array.from({ length: n }, (_, i) => {
      const x = (i / (n - 1)) * (g.length - 1), a = Math.floor(x), b = Math.min(a + 1, g.length - 1);
      return g[a] + (g[b] - g[a]) * (x - a);
    });
    const N = 7;
    function path(g) {
      const pts = g.map((v, i) => [(i / (g.length - 1)) * W, Math.max(8, Math.min(212, MID - v * DB))]);
      let p = 'M' + pts[0][0].toFixed(1) + ' ' + pts[0][1].toFixed(1);
      for (let i = 0; i < pts.length - 1; i++) {
        const p0 = pts[i - 1] || pts[i], p1 = pts[i], p2 = pts[i + 1], p3 = pts[i + 2] || p2;
        const c1 = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6];
        const c2 = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6];
        p += ' C' + c1.map((n) => n.toFixed(1)).join(' ') + ' ' + c2.map((n) => n.toFixed(1)).join(' ') + ' ' + p2.map((n) => n.toFixed(1)).join(' ');
      }
      return p;
    }
    function draw(g) {
      const p = path(g);
      curve.setAttribute('d', p);
      if (area) area.setAttribute('d', p + ' L' + W + ' 220 L0 220 Z');
    }
    let now = resample(gains(chips[0]), N), raf = 0;
    function morph(to) {
      cancelAnimationFrame(raf);
      if (reduced()) { now = to; draw(now); return; }
      const from = now.slice(), t0 = performance.now(), dur = 520;
      const step = (t) => {
        const k = Math.min(1, (t - t0) / dur), e = 1 - Math.pow(1 - k, 4);
        now = from.map((v, i) => v + (to[i] - v) * e);
        draw(now);
        if (k < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    }

    let items = null, selected = chips[0];
    const counts = new Map();
    function countFor(value) {
      if (!items) return null;
      if (!counts.has(value)) {
        counts.set(value, items.filter((p) => (!d.tag || p.tech.includes(d.tag)) && (p.signature === value || p.secondary.includes(value))
          && p.variants.some((v) => v.available)).length);
      }
      return counts.get(value);
    }
    function show(chip) {
      morph(resample(gains(chip), N));
      if (line) line.textContent = chip.dataset.text || '';
      const label = chip.dataset.label;
      if (ctaLabel) ctaLabel.textContent = fill(d.cta, { sound: label });
      if (cta) {
        if (d.tag) { cta.dataset.value = chip.dataset.bridgeSound; cta.href = '?signature=' + encodeURIComponent(chip.dataset.bridgeSound) + '#products'; }
        else cta.href = chip.dataset.url || '#';
      }
      const n = countFor(chip.dataset.bridgeSound);
      if (countOut) {
        countOut.textContent = n === null ? '' : n === 0 ? d.none : n === 1 ? d.countOne : fill(d.count, { count: n });
        countOut.hidden = n === null;
      }
    }
    function select(chip) {
      selected = chip;
      chips.forEach((c) => c.setAttribute('aria-pressed', String(c === chip)));
      show(chip);
    }
    chips.forEach((c) => c.setAttribute('role', 'button'));
    el.addEventListener('click', (e) => {
      const chip = e.target.closest('[data-bridge-sound]');
      if (!chip || e.metaKey || e.ctrlKey || e.shiftKey) return;
      e.preventDefault();
      select(chip);
    });
    el.addEventListener('keydown', (e) => {
      const chip = e.target.closest('[data-bridge-sound]');
      if (!chip) return;
      if (e.key === ' ') { e.preventDefault(); select(chip); return; }
      if (!['ArrowLeft', 'ArrowRight'].includes(e.key)) return;
      e.preventDefault();
      const next = chips[(chips.indexOf(chip) + (e.key === 'ArrowRight' ? 1 : -1) + chips.length) % chips.length];
      next.focus();
      select(next);
    });
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      chips.forEach((c) => c.addEventListener('pointerenter', () => show(c)));
      el.querySelector('.lt-banner__chips').addEventListener('pointerleave', () => show(selected));
    }
    el.classList.add('is-live');
    draw(now);
    show(selected);
    loadCatalog(d.feed || '/collections/all').then((list) => { items = list; show(selected); }).catch(() => {});
  }

  function start() {
    initHub();
    document.querySelectorAll('[data-lt-bridge]').forEach(initBridge);
    document.querySelectorAll('[data-lt-products]').forEach(initGuide);
    document.querySelectorAll('.ls-tech').forEach((n) => n.classList.add('lt-enhanced'));
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
